import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminSupplierProducts() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [form, setForm] = useState(defaults());

  useEffect(() => {
    api.get("/products", { params: { limit: 200 } }).then(({ data }) => setProducts(data.items));
    api.get("/admin/suppliers").then(({ data }) => setSuppliers(data.suppliers));
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadOffers(selected);
    rank(selected);
  }, [selected]);

  const loadOffers = (pid) =>
    api.get("/admin/supplier-products", { params: { product_id: pid } }).then(({ data }) => setOffers(data.items));

  const rank = (pid) =>
    api.get(`/admin/rank/${pid}?quantity=1`).then(({ data }) => setRanking(data));

  const upsert = async () => {
    if (!selected || !form.supplier_id || !form.supplier_sku) {
      toast.error("Product, supplier, and SKU are required");
      return;
    }
    try {
      await api.post("/admin/supplier-products", { ...form, product_id: selected });
      toast.success("Supplier product saved");
      setForm(defaults()); loadOffers(selected); rank(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!confirm("Remove this supplier mapping?")) return;
    await api.delete(`/admin/supplier-products/${id}`);
    loadOffers(selected); rank(selected);
  };

  return (
    <div className="container-tight py-8" data-testid="admin-supplier-products-page">
      <AdminNav />
      <AdminHeader title="SUPPLIER MAPPING" subtitle="Map an Interstate PC product to one or more supplier SKUs. Seed data is marked verified=false." />

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <aside className="border border-zinc-800 bg-[#0a0a0c] rounded-sm p-3 max-h-[70vh] overflow-y-auto">
          <div className="font-mono text-xs tracking-widest text-zinc-400 uppercase mb-3">Product</div>
          {products.map((p) => (
            <button key={p.id} data-testid={`pick-${p.id}`}
              onClick={() => setSelected(p.id)}
              className={`w-full text-left px-2 py-2 text-xs border-b border-zinc-900 flex items-center gap-2 ${selected === p.id ? "bg-[#FF5C00]/10 text-[#FF5C00]" : "text-zinc-300 hover:bg-zinc-900"}`}>
              <img src={p.image} className="w-8 h-8 object-cover border border-zinc-800" alt="" />
              <div className="flex-1 truncate">{p.name}</div>
              <span className="font-mono text-[10px] text-zinc-500">{formatPrice(p.price)}</span>
            </button>
          ))}
        </aside>

        <div className="space-y-6">
          {!selected ? (
            <div className="border border-zinc-800 bg-[#0a0a0c] p-8 rounded-sm text-center text-zinc-500 font-mono">
              SELECT A PRODUCT TO MANAGE ITS SUPPLIER MAPPINGS
            </div>
          ) : (
            <>
              <section className="border border-zinc-800 bg-[#0a0a0c] rounded-sm">
                <div className="px-4 py-3 border-b border-zinc-800 font-mono text-xs tracking-widest uppercase text-zinc-300">
                  Supplier Offers for <span className="text-[#00F0FF]">{selected}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900">
                      <th className="p-2 text-left">Supplier</th>
                      <th className="p-2 text-left">SKU</th>
                      <th className="p-2 text-right">Cost</th>
                      <th className="p-2 text-right">Ship</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2 text-left">Days</th>
                      <th className="p-2 text-left">Verified</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.length === 0 && (
                      <tr><td colSpan={8} className="p-4 text-center text-zinc-500 font-mono">NO OFFERS</td></tr>
                    )}
                    {offers.map((o) => (
                      <tr key={o.id} className="border-b border-zinc-900" data-testid={`offer-${o.supplier_sku}`}>
                        <td className="p-2">{o.supplier_name}</td>
                        <td className="p-2 font-mono text-xs">{o.supplier_sku}</td>
                        <td className="p-2 font-mono text-right">{formatPrice(o.cost)}</td>
                        <td className="p-2 font-mono text-right">{formatPrice(o.shipping_cost)}</td>
                        <td className="p-2 font-mono text-right">{o.available_qty}</td>
                        <td className="p-2 font-mono text-xs">{o.estimated_ship_days_min}-{o.estimated_ship_days_max}d</td>
                        <td className="p-2 font-mono text-[10px]">{o.is_verified ? <span className="text-green-400">verified</span> : <span className="text-yellow-400">seed/mock</span>}</td>
                        <td className="p-2"><button onClick={() => del(o.id)} className="text-red-400 hover:text-red-300 text-xs">remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {ranking && (
                <section className="border border-zinc-800 bg-[#0a0a0c] rounded-sm">
                  <div className="px-4 py-3 border-b border-zinc-800 font-mono text-xs tracking-widest uppercase text-zinc-300">
                    Selection Engine Preview (qty=1) — selling {formatPrice(ranking.selling_price)}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900">
                        <th className="p-2 text-left">Rank</th>
                        <th className="p-2 text-left">Supplier</th>
                        <th className="p-2 text-right">Score</th>
                        <th className="p-2 text-right">Profit</th>
                        <th className="p-2 text-right">Margin</th>
                        <th className="p-2 text-left">Eligible</th>
                        <th className="p-2 text-left">Reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.ranked.map((r, i) => (
                        <tr key={i} className="border-b border-zinc-900" data-testid={`rank-${r.supplier_code}`}>
                          <td className="p-2 font-mono">#{i + 1}</td>
                          <td className="p-2">{r.supplier_name}</td>
                          <td className="p-2 font-mono text-right">{r.score.toFixed(4)}</td>
                          <td className="p-2 font-mono text-right">{formatPrice(r.pricing.contribution_profit)}</td>
                          <td className="p-2 font-mono text-right">{r.pricing.margin_percent}%</td>
                          <td className="p-2">{r.eligible ? <span className="text-green-400">yes</span> : <span className="text-red-400">no</span>}</td>
                          <td className="p-2 font-mono text-[10px] text-zinc-500">{r.reasons.join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              <section className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm">
                <div className="font-mono text-xs tracking-widest uppercase text-zinc-300 mb-3">Add / Update Supplier Offer</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="font-mono text-xs tracking-widest text-zinc-400 uppercase">Supplier</Label>
                    <Select value={form.supplier_id} onValueChange={(v) => setForm({...form, supplier_id: v})}>
                      <SelectTrigger data-testid="offer-supplier" className="bg-[#050505] border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0c] border-zinc-800">
                        {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <F label="Supplier SKU" v={form.supplier_sku} on={(v) => setForm({...form, supplier_sku: v})} tid="offer-sku" />
                  <F label="MFR Part Number" v={form.mfr_part_number || ""} on={(v) => setForm({...form, mfr_part_number: v})} tid="offer-mpn" />
                  <F label="UPC" v={form.upc || ""} on={(v) => setForm({...form, upc: v})} tid="offer-upc" />
                  <F label="Cost ($)" type="number" v={form.cost} on={(v) => setForm({...form, cost: Number(v)})} tid="offer-cost" />
                  <F label="Shipping ($)" type="number" v={form.shipping_cost} on={(v) => setForm({...form, shipping_cost: Number(v)})} tid="offer-ship" />
                  <F label="MSRP ($)" type="number" v={form.msrp || ""} on={(v) => setForm({...form, msrp: Number(v)})} tid="offer-msrp" />
                  <F label="Available Qty" type="number" v={form.available_qty} on={(v) => setForm({...form, available_qty: Number(v)})} tid="offer-qty" />
                  <F label="Ship Days Min" type="number" v={form.estimated_ship_days_min} on={(v) => setForm({...form, estimated_ship_days_min: Number(v)})} tid="offer-dmin" />
                  <F label="Ship Days Max" type="number" v={form.estimated_ship_days_max} on={(v) => setForm({...form, estimated_ship_days_max: Number(v)})} tid="offer-dmax" />
                  <F label="Warehouse" v={form.warehouse || ""} on={(v) => setForm({...form, warehouse: v})} tid="offer-warehouse" />
                </div>
                <Button data-testid="save-offer" onClick={upsert}
                  className="mt-4 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
                  SAVE OFFER
                </Button>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function defaults() {
  return { supplier_id: "", supplier_sku: "", cost: 0, shipping_cost: 8.99,
           available_qty: 10, estimated_ship_days_min: 1, estimated_ship_days_max: 5 };
}

function F({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <Label className="font-mono text-xs tracking-widest text-zinc-400 uppercase">{label}</Label>
      <Input data-testid={tid} type={type} value={v} onChange={(e) => on(e.target.value)}
        className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
    </div>
  );
}
